import {
  getBackendLanguage,
  getLanguageDisplayName,
  getOrgLanguageCodes,
  translateBackend,
} from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { List, ListItem } from '@mui/material';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { Dialog, DialogTitle } from '@ovComponents/2-component/dialog/dialog';
import { Button } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import RichTextEditor from '../richTextEditor/richTextEditor';

export type TranslatableRichTextEditorArgs = {
  value?: TranslatedString;
  setValue: (value: TranslatedString) => void;
  id?: string;
  sx?: any;
  minHeight?: string | number;
  label?: string;
  fullToolbar?: boolean;
  testId?: string;
};

const TranslatableRichTextEditor = ({
  value = getLanguageDefaults(),
  setValue,
  id,
  sx,
  minHeight,
  label,
  fullToolbar,
  testId = 'translatable-rich-text-editor',
}: TranslatableRichTextEditorArgs) => {
  const { t } = useTranslation('components');
  const [open, setOpen] = useState(false);
  const hasMultipleLanguages = getOrgLanguageCodes().length > 1;

  return (
    <>
      <RichTextEditor
        testId={`${testId}-outter`}
        value={translateBackend(value)}
        setValue={(newValue) =>
          setValue({ ...value, [getBackendLanguage()]: newValue })
        }
        id={id}
        sx={sx}
        minHeight={minHeight}
        label={label}
        fullToolbar={fullToolbar}
        type={hasMultipleLanguages ? 'multiLanguage' : 'default'}
        onTranslate={hasMultipleLanguages ? () => setOpen(!open) : undefined}
      />
      <Dialog onClose={() => setOpen(false)} open={open} fullWidth>
        <DialogTitle>
          {t('translate')} - {label}
        </DialogTitle>
        <List>
          {getOrgLanguageCodes().map((lang) => (
            <ListItem key={lang}>
              <RichTextEditor
                testId={`${testId}-inner-${lang}`}
                value={value?.[lang as keyof typeof value] ?? ''}
                setValue={(newValue) =>
                  setValue({ ...value, [lang]: newValue })
                }
                label={getLanguageDisplayName(lang)}
                fullToolbar={fullToolbar}
              />
            </ListItem>
          ))}
          <ListItem>
            <Button
              data-testid={`${testId}-done-button`}
              fullWidth
              variant='contained'
              onClick={() => setOpen(false)}
            >
              {t('done')}
            </Button>
          </ListItem>
        </List>
      </Dialog>
    </>
  );
};

export default TranslatableRichTextEditor;
